// packages/agent-kit/src/hooks/admin/useManageGoals.ts

import { useState, useCallback } from 'react';
import useSWR from 'swr';
import { adminGoalService, isApiError } from '@dopomogai/api-client';
import type { Goal, GoalSnapshot } from '@dopomogai/types';
import { AgentKitError } from '../../types';


const goalDefinitionsFetcher = async (_key: string): Promise<Goal[]> => {
  const response = await adminGoalService.listGoalDefinitions();
  if (isApiError(response)) {
      // SWR expects the fetcher to throw on error
      throw new AgentKitError('Failed to fetch goal definitions', response);
  }
  return response;
};


/**
 * Hook to fetch the list of all goal definitions.
 * The admin UI will use this to know which goals exist and which are manual.
 */
export const useGoalDefinitions = () => {
  const { data, error, isLoading, mutate } = useSWR<Goal[], AgentKitError>(
    '/admin/goals/definitions',
    // Now we pass our clearly defined fetcher function.
    goalDefinitionsFetcher 
  );

  return {
    goals: data,
    isLoading,
    error,
    mutate,
  };
};

/**
 * Hook to provide a function for logging a manual goal snapshot.
 * This is a "mutation" hook and does not fetch data itself.
 */
export const useLogGoalSnapshot = () => {
  const [isLogging, setIsLogging] = useState(false);
  const [error, setError] = useState<AgentKitError | null>(null);

  const logSnapshot = useCallback(async (goalId: string, value: number): Promise<GoalSnapshot | null> => {
    setIsLogging(true);
    setError(null);

    const res = await adminGoalService.logManualSnapshot(goalId, { value });

    if (isApiError(res)) {
      setError(new AgentKitError(`Failed to log snapshot for ${goalId}`, res));
      setIsLogging(false);
      return null;
    }

    setIsLogging(false);
    return res;
  }, []);

  return {
    logSnapshot,
    isLogging,
    error,
  };
};